import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  MutableRefObject,
  useContext,
  useMemo,
} from 'react'
import { Text as RNText, Pressable, Animated, View } from 'react-native'
import { useField } from 'formik'

import { View as TamaguiView } from 'tamagui'

import { FormFieldError } from './FormFieldError'
import { FormMarkdownSupportedIcon } from './FormMarkdownSupportedIcon'
import { Stack, StackProps } from '../Stack'
import { Element } from '../Element'
import { Text } from '../Text'
import { TokensContext } from '../context'

import { FormFieldContext } from './shared'

export { FormFieldContext }

export interface FormFieldProps {
  hint?: string
  label?: string
  markdownSupported?: boolean
  name: string
  showInputWrapper?: boolean
  showError?: boolean
  size?: number
  variant?: string
  expand?: boolean
  inputWrapperProps?: StackProps
}

// export const FormFieldContext = createContext<{
//   name: string
//   formElementRef: MutableRefObject<any>
//   onFocus?: () => void
//   onBlur?: () => void
// }>(undefined as any)

export const FormField = ({
  name,
  children,
  hint,
  label,
  markdownSupported = false,
  showInputWrapper = true,
  showError = true,
  size = 4,
  variant,
  expand = false,
  inputWrapperProps = { children: undefined },
}: FormFieldProps & { children: React.ReactNode }) => {
  const tokens = useContext(TokensContext)
  const focusedFade = useRef(new Animated.Value(0)).current
  const formElementRef = useRef<{ type?: string; focus?: () => void; setFocus?: () => void; click?: () => void } | null>(null)

  const [focused, setFocused] = useState(false)

  const [field, fieldMeta, helpers] = useField(name)

  const onFocus = useCallback(() => {
    setFocused(true)

    Animated.timing(focusedFade, {
      toValue: 1,
      duration: 250,
      useNativeDriver: false,
    }).start()
  }, [setFocused, helpers])

  const onBlur = useCallback(() => {
    setFocused(false)
    helpers.setTouched(true)

    Animated.timing(focusedFade, {
      toValue: 0,
      duration: 250,
      useNativeDriver: false,
    }).start()
  }, [setFocused])

  const formInputWrapperStyle = useMemo(
    () => ({
      maxWidth: '100%',
      borderColor: focusedFade.interpolate({
        inputRange: [0, 1],
        outputRange: [tokens.color.grey[5], tokens.color.darkBlue[9]],
      }),
    }),
    [focusedFade, tokens]
  )

  const focusViewStyle = useMemo(() => {
    if (focused && expand) {
      return { zIndex: 999999, flex: 1 }
    }

    if (focused) {
      return { zIndex: 999999 }
    }

    if (expand) {
      return { flex: 1 }
    }

    return {}
  }, [focused, expand])

  return (
    <View style={focusViewStyle}>
      <Stack>
        <FormFieldContext.Provider
          value={{ name, formElementRef, onFocus, onBlur }}
        >
          <TamaguiView
            focusable={false}
            onPress={() => {
              const formElement = formElementRef.current

              // HTML File Inputs don't open the dialog on focus, only click
              // if (formElement && formElement.type === 'file') {
              //   formElement.click()
              //   return
              // }

              if (formElement && formElement.type === 'checkbox') {
                helpers.setValue(!field.value)
                return
              }

              if (formElement && typeof formElement.focus === 'function') {
                formElement.focus()
                return
              }

              if (formElement && typeof formElement.setFocus === 'function') {
                formElement.setFocus()
                return
              }

              Animated.timing(focusedFade, {
                toValue: 1,
                duration: 250,
                useNativeDriver: false,
              }).start(() => {
                Animated.timing(focusedFade, {
                  toValue: 0,
                  duration: 250,
                  useNativeDriver: false,
                }).start()
              })
            }}
          >
            <Element component='FormField' as={View} variant={variant}>
              <Stack gap={1}>
                <Stack alignItems='center' direction='horizontal'>
                  <Stack direction='horizontal' expand gap={2}>
                    {!!label && (
                      <Element
                        as={RNText}
                        tokens={{ fontSize: size, lineHeight: size }}
                        component='FormFieldLabel'
                      >
                        {label}
                      </Element>
                    )}
                    {showError && <FormFieldError size={size} />}
                  </Stack>

                  {markdownSupported && <FormMarkdownSupportedIcon />}
                </Stack>

                <Stack {...inputWrapperProps}>
                  {showInputWrapper ? (
                    <Element
                      as={Animated.View}
                      component='FormInputWrapper'
                      // @ts-expect-error - Animated.interpolate returns AnimatedInterpolation which isn't compatible with StyleProp
                      style={formInputWrapperStyle}
                    >
                      {children}
                    </Element>
                  ) : (
                    children
                  )}
                </Stack>

                {!!hint &&
                  hint.split('\n').map((hintLine, index) => (
                    <Text key={index} size={4} variant='formHint'>
                      {hintLine}
                    </Text>
                  ))}
              </Stack>
            </Element>
          </TamaguiView>
        </FormFieldContext.Provider>
      </Stack>
    </View>
  )
}
